import React, { useState, useEffect } from 'react';
import calendarService from '../../services/calendarService';
import { useLiveStatus } from '../../contexts/LiveStatusContext';
import {
  LiveTv as LiveIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  PersonOutline as PersonOutlineIcon,
} from '@mui/icons-material';

/**
 * CalendarList Component
 * Displays events in a chronological list format grouped by date
 */
const CalendarList = ({ events, onEventSponsor, theme, showOnlyUpcoming = false, isPastMonth = false, currentMonth, currentYear, occasionMap }) => {
  const liveContext = useLiveStatus();
  const hasLiveVideos = liveContext?.hasLiveVideos || false;
  const [expandedEvents, setExpandedEvents] = useState({});

  const hasEventDetails = (event) => {
    return !!(event.description || event.metadata?.sponsor_name);
  };

  const toggleEventDetails = (eventId) => {
    setExpandedEvents(prev => ({ ...prev, [eventId]: !prev[eventId] }));
  };
  
  // Add subtle pulse animation on mount for sponsor buttons
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes subtlePulse {
        0% { transform: scale(1); }
        50% { transform: scale(1.05); }
        100% { transform: scale(1); }
      }
      .sponsor-btn-animate {
        animation: subtlePulse 0.5s ease-out;
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);
  // Filter events if showing only upcoming
  let filteredEvents = events;
  if (showOnlyUpcoming) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    filteredEvents = events.filter(event => {
      const eventDate = new Date(event.start_date + 'T00:00:00');
      return eventDate >= today;
    });
  }
  
  // Group events by date
  const groupedEvents = calendarService.groupEventsByDate(filteredEvents);

  // Inject occasion-only dates (dates with Islamic occasions but no center events)
  if (occasionMap && occasionMap.size > 0) {
    const existingDates = new Set(groupedEvents.map(g => g.date));
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    occasionMap.forEach((data, dateStr) => {
      if (data.occasions && data.occasions.length > 0 && !existingDates.has(dateStr)) {
        const dateObj = new Date(dateStr + 'T00:00:00');
        // For upcoming view, only include future dates
        if (showOnlyUpcoming && dateObj < today) return;

        groupedEvents.push({
          date: dateStr,
          dayName: dateObj.toLocaleDateString('en-US', { weekday: 'long' }),
          isToday: dateObj.toDateString() === today.toDateString(),
          isPast: dateObj < today,
          events: [], // No center events — only Islamic occasion
        });
      }
    });

    // Re-sort by date after injecting occasion-only dates
    groupedEvents.sort((a, b) => new Date(a.date + 'T00:00:00') - new Date(b.date + 'T00:00:00'));
  }

  // Emoji icon from centralized category map
  const getEventEmoji = (eventType) => {
    const emojis = calendarService.constructor.EVENT_TYPE_EMOJIS;
    return emojis[eventType] || emojis.general;
  };
  
  // Check if an event is currently live
  const isEventLive = (event) => {
    if (!hasLiveVideos) return false;
    const now = new Date();
    const eventDate = new Date(event.start_date + 'T' + event.start_time);
    // Check if event started within last 2 hours (typical event duration)
    const timeDiff = (now - eventDate) / (1000 * 60); // difference in minutes
    return timeDiff >= 0 && timeDiff <= 120; // Currently happening or started within 2 hours
  };
  
  // Check if we're on mobile
  const isMobile = window.innerWidth <= 768;
  
  // Styles
  const styles = {
    container: {
      width: '100%',
      maxWidth: '1200px',
      margin: '0 auto'
    },
    emptyState: {
      textAlign: 'center',
      padding: '60px 20px',
      color: theme?.colors?.cardTextSecondary || '#64748b'
    },
    emptyIcon: {
      fontSize: '48px',
      marginBottom: '16px',
      opacity: 0.5
    },
    emptyTitle: {
      fontSize: '18px',
      fontWeight: '500',
      marginBottom: '8px',
      color: theme?.colors?.cardText || '#333'
    },
    emptyText: {
      fontSize: '14px',
      color: theme?.colors?.cardTextSecondary || '#64748b'
    },
    dateGroup: {
      marginBottom: '16px'
    },
    dateHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      marginBottom: '8px',
      padding: '6px 0',
      borderBottom: '1px solid #e2e8f0'
    },
    dateNumber: {
      fontSize: '20px',
      fontWeight: '600',
      color: theme?.colors?.primary || '#0f172a',
      minWidth: '60px',
      textAlign: 'left'
    },
    dateInfo: {
      flex: 1
    },
    dayName: {
      fontSize: '16px',
      fontWeight: '500',
      color: theme?.colors?.cardText || '#333'
    },
    fullDate: {
      fontSize: '13px',
      color: theme?.colors?.cardTextSecondary || '#64748b'
    },
    todayBadge: {
      backgroundColor: theme?.colors?.primary || '#0f172a',
      color: 'white',
      padding: '4px 12px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: '500'
    },
    eventsList: {
      display: 'flex',
      flexDirection: 'column',
      gap: '6px'
    },
    eventCard: {
      display: 'flex',
      alignItems: 'stretch',
      backgroundColor: 'white',
      border: '1px solid #e2e8f0',
      borderRadius: '8px',
      overflow: 'hidden',
      transition: 'all 0.2s',
      cursor: 'pointer'
    },
    eventCardHover: {
      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
      transform: 'translateY(-2px)'
    },
    eventColorBar: {
      width: '4px',
      flexShrink: 0
    },
    eventContent: {
      flex: 1,
      padding: isMobile ? '8px' : '10px 12px',
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: '8px'
    },
    eventTime: {
      width: isMobile ? '65px' : '80px',
      fontSize: '14px',
      fontWeight: '500',
      color: theme?.colors?.cardText || '#333',
      flexShrink: 0,
      textAlign: 'left'
    },
    eventInfo: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: '16px',
      flex: 1,
      minWidth: 0
    },
    eventDetails: {
      flex: 1,
      minWidth: 0
    },
    eventTitle: {
      fontSize: isMobile ? '14px' : '15px',
      fontWeight: '500',
      color: theme?.colors?.cardText || '#333',
      marginBottom: '2px',
      display: 'flex',
      alignItems: 'center'
    },
    eventType: {
      display: 'inline-block',
      fontSize: '12px',
      padding: '2px 8px',
      borderRadius: '12px',
      backgroundColor: '#f1f5f9',
      color: theme?.colors?.cardTextSecondary || '#64748b',
      marginBottom: '4px'
    },
    eventDescription: {
      fontSize: '13px',
      color: theme?.colors?.cardTextSecondary || '#64748b',
      lineHeight: '1.4'
    },
    sponsorButton: {
      backgroundColor: `${theme?.colors?.primary || '#0f172a'}08`,
      color: theme?.colors?.primary || '#0f172a',
      border: 'none',
      padding: isMobile ? '4px 8px' : '5px 10px',
      borderRadius: '6px',
      fontSize: isMobile ? '11px' : '12px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s',
      alignSelf: 'center',
      whiteSpace: 'nowrap',
      marginLeft: '8px',
      flexShrink: 0,
      opacity: 0.7,
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px'
    },
    sponsorButtonHover: {
      backgroundColor: `${theme?.colors?.primary || '#0f172a'}18`,
      opacity: 1,
      transform: 'scale(1.05)'
    },
    pastEvent: {
      opacity: 0.6
    }
  };

  // Render empty state
  if (groupedEvents.length === 0) {
    return (
      <div style={styles.container}>
        <div style={styles.emptyState}>
          <div style={styles.emptyIcon}>📅</div>
          <div style={styles.emptyTitle}>No Events This Month</div>
          <div style={styles.emptyText}>
            {isPastMonth
              ? 'There were no programs recorded for this month.'
              : 'Check back later or browse other months for upcoming programs.'}
          </div>
        </div>
      </div>
    );
  }


  return (
    <div style={styles.container}>
      {groupedEvents.map((dateGroup) => {
        return (
          <React.Fragment key={dateGroup.date}>
            {/* Date Group */}
            <div style={styles.dateGroup}>
              {/* Date Header */}
              <div style={styles.dateHeader}>
                <div style={styles.dateNumber}>
                  {new Date(dateGroup.date + 'T00:00:00').getDate()}, {dateGroup.dayName.slice(0, 3)}
                </div>
                {/* Hijri date badge */}
                {occasionMap && (() => {
                  const data = occasionMap.get(dateGroup.date);
                  if (!data?.hijri) return null;
                  const { hijri } = data;
                  const isNewMonth = hijri.day === 1;
                  return (
                    <div style={{
                      fontSize: '12px',
                      color: theme?.colors?.cardTextSecondary || '#64748b',
                      background: isNewMonth ? '#e0f2fe' : '#f1f5f9',
                      padding: '2px 8px', borderRadius: '8px',
                      fontWeight: isNewMonth ? 600 : 400,
                    }}>
                      {isNewMonth ? `1 ${hijri.monthName} ${hijri.year}` : `${hijri.day} ${hijri.monthAbbr}`}
                    </div>
                  );
                })()}
                {dateGroup.isToday && (
                  <div style={styles.todayBadge}>TODAY</div>
                )}
              </div>

              {/* Islamic occasion cards for this date */}
              {occasionMap && (() => {
                const data = occasionMap.get(dateGroup.date);
                if (!data?.occasions || data.occasions.length === 0) return null;
                return data.occasions.map((occasion, i) => {
                  const isMourning = occasion.type === 'mourning';
                  const isJoyous = occasion.type === 'joyous';
                  return (
                    <div key={`occasion-${i}`} style={{
                      display: 'flex', alignItems: 'stretch',
                      borderRadius: '8px', overflow: 'hidden', marginBottom: '6px',
                      background: isMourning
                        ? 'linear-gradient(135deg, #1a1a1a, #2d2d2d)'
                        : isJoyous
                        ? 'linear-gradient(135deg, #f0fdf4, #fefce8)'
                        : 'linear-gradient(135deg, #eff6ff, #f5f3ff)',
                      border: isMourning ? '1px solid #333'
                        : isJoyous ? '1px solid #bbf7d0'
                        : '1px solid #bfdbfe',
                    }}>
                      <div style={{
                        width: '4px', flexShrink: 0,
                        background: isMourning ? '#555' : isJoyous ? (theme?.colors?.primary || '#22c55e') : '#3b82f6',
                      }} />
                      <div style={{
                        flex: 1, padding: '10px 12px',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px',
                      }}>
                        <div>
                          <div style={{
                            fontSize: '14px', fontWeight: 500,
                            color: isMourning ? '#f0f0f0' : (theme?.colors?.cardText || '#333'),
                          }}>
                            {isMourning ? '🏴' : isJoyous ? '🎉' : '✨'} {occasion.nameEn}
                          </div>
                          {occasion.significance && (
                            <div style={{
                              fontSize: '12px', marginTop: '2px',
                              color: isMourning ? '#aaa' : (theme?.colors?.cardTextSecondary || '#475569'),
                            }}>
                              {occasion.significance}
                            </div>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 }}>
                          <span style={{
                            fontSize: '11px', padding: '2px 8px', borderRadius: '8px', fontWeight: 500,
                            background: isMourning ? '#333' : isJoyous ? '#dcfce7' : '#dbeafe',
                            color: isMourning ? '#aaa' : isJoyous ? '#166534' : '#1e40af',
                          }}>
                            {occasion.type === 'mourning' ? 'Mourning' : occasion.type === 'joyous' ? 'Joyous' : 'Significant'}
                          </span>
                          {occasion.amaalUrlPrimary && (
                            <a href={occasion.amaalUrlPrimary} target="_blank" rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              style={{
                                fontSize: '12px', textDecoration: 'none', padding: '4px 10px',
                                borderRadius: '6px', whiteSpace: 'nowrap',
                                background: isMourning ? '#444' : isJoyous ? (theme?.colors?.primary || '#166534') : '#1e40af',
                                color: isMourning ? '#ddd' : '#fff',
                                transition: 'opacity 0.2s',
                              }}>
                              View Amaal →
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                });
              })()}

              {/* Events List */}
              <div style={styles.eventsList}>
                {dateGroup.events.map(event => (
                  <div
                    key={event.id}
                    style={{
                      ...styles.eventCard,
                      ...(event.isPast ? styles.pastEvent : {})
                    }}
                    onMouseEnter={(e) => {
                      if (!event.isPast) {
                        Object.assign(e.currentTarget.style, styles.eventCardHover);
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow = '';
                      e.currentTarget.style.transform = '';
                    }}
                  >
                    {/* Color Bar */}
                    <div 
                      style={{
                        ...styles.eventColorBar,
                        backgroundColor: event.color
                      }}
                    />
                    
                    {/* Event Content */}
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                      <div style={styles.eventContent}>
                        <div style={styles.eventTime}>
                          {event.displayTime}
                        </div>
                        <div style={styles.eventDetails}>
                          <div style={styles.eventTitle}>
                            <span style={{ marginRight: '8px', fontSize: '18px' }}>{getEventEmoji(event.event_type)}</span>
                            {event.title}
                            {isEventLive(event) && (
                              <span style={{
                                marginLeft: '8px',
                                backgroundColor: '#ef4444',
                                color: 'white',
                                padding: '2px 6px',
                                borderRadius: '4px',
                                fontSize: '11px',
                                fontWeight: '600',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '3px'
                              }}>
                                <LiveIcon style={{ fontSize: '12px' }} />
                                LIVE
                              </span>
                            )}
                            {hasEventDetails(event) && (
                              <span
                                onClick={(e) => { e.stopPropagation(); toggleEventDetails(event.id); }}
                                style={{
                                  marginLeft: '6px',
                                  cursor: 'pointer',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  color: theme?.colors?.primary || '#0f172a',
                                  opacity: 0.6,
                                }}
                                title="Show details"
                              >
                                {expandedEvents[event.id]
                                  ? <ExpandLessIcon style={{ fontSize: '18px' }} />
                                  : <ExpandMoreIcon style={{ fontSize: '18px' }} />}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Sponsor Button — hidden on past events (can't sponsor what already happened) */}
                        {event.canSponsor && !event.isPast && onEventSponsor && (
                          <button
                            style={styles.sponsorButton}
                            onClick={(e) => {
                              e.stopPropagation();
                              onEventSponsor(event);
                            }}
                            onMouseEnter={(e) => {
                              Object.assign(e.target.style, styles.sponsorButtonHover);
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = `${theme?.colors?.primary || '#0f172a'}08`;
                              e.target.style.opacity = '0.7';
                              e.target.style.transform = '';
                            }}
                            title={`Support this ${event.event_type || 'event'}`}
                          >
                            <span style={{ fontSize: isMobile ? '14px' : '14px' }}>💝</span>
                            {!isMobile && <span>Sponsor</span>}
                          </button>
                        )}
                      </div>

                      {/* Expandable Details Section */}
                      {hasEventDetails(event) && expandedEvents[event.id] && (
                        <div style={{
                          padding: '8px 12px 10px',
                          paddingLeft: isMobile ? '16px' : (80 + 12 + 12) + 'px',
                          borderTop: '1px solid #f1f5f9',
                          backgroundColor: '#fafbfc',
                          fontSize: '13px',
                          lineHeight: '1.5',
                          color: theme?.colors?.cardTextSecondary || '#64748b',
                        }}>
                          {event.metadata?.sponsor_name && (
                            <div style={{
                              display: 'inline-flex', alignItems: 'center', gap: '6px',
                              marginBottom: event.description ? '8px' : 0,
                              padding: '4px 10px',
                              borderRadius: '6px',
                              backgroundColor: `${theme?.colors?.primary || '#0f172a'}10`,
                              border: `1px solid ${theme?.colors?.primary || '#0f172a'}20`,
                            }}>
                              <PersonOutlineIcon style={{ fontSize: '15px', color: theme?.colors?.primary || '#0f172a' }} />
                              <span style={{ fontWeight: '600', fontSize: '12px', color: theme?.colors?.primary || '#0f172a' }}>
                                Sponsored by {event.metadata.sponsor_name}
                              </span>
                            </div>
                          )}
                          {event.description && (
                            <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
                              {event.description}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default CalendarList;